import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { CloudRain } from '@phosphor-icons/react';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

const RegisterPage = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [acceptedDisclaimer, setAcceptedDisclaimer] = useState(false);
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register(email, password, name);
      toast.success('Account created successfully');
      navigate('/dashboard');
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Registration failed';
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-6">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2 mb-4">
            <CloudRain size={32} weight="bold" className="text-blue-600" />
            <span className="text-2xl font-bold text-slate-950" style={{ fontFamily: 'Chivo' }}>WeatherProof</span>
          </Link>
          <h1 className="text-3xl font-bold text-slate-950 mt-6" style={{ fontFamily: 'Chivo' }}>Sign Up</h1>
          <p className="text-sm text-slate-600 mt-2">Start verifying weather data today</p>
        </div>

        <div className="bg-white border border-slate-200 p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-xs font-medium uppercase tracking-[0.2em] text-slate-500 mb-2">
                Full Name
              </label>
              <input
                data-testid="register-name-input"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                className="w-full px-4 py-2 border border-slate-300 focus:ring-2 focus:ring-blue-600 focus:ring-offset-2 focus:outline-none text-sm"
                placeholder="John Doe"
              />
            </div>
            <div>
              <label className="block text-xs font-medium uppercase tracking-[0.2em] text-slate-500 mb-2">
                Email Address
              </label>
              <input
                data-testid="register-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full px-4 py-2 border border-slate-300 focus:ring-2 focus:ring-blue-600 focus:ring-offset-2 focus:outline-none text-sm"
                placeholder="you@example.com"
              />
            </div>
            <div>
              <label className="block text-xs font-medium uppercase tracking-[0.2em] text-slate-500 mb-2">
                Password
              </label>
              <input
                data-testid="register-password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={6}
                className="w-full px-4 py-2 border border-slate-300 focus:ring-2 focus:ring-blue-600 focus:ring-offset-2 focus:outline-none text-sm"
                placeholder="••••••••"
              />
            </div>
            
            {/* Terms of Service Summary */}
            <div className="p-4 bg-slate-50 border border-slate-200 text-xs text-slate-600 leading-relaxed mb-4">
              <p className="font-semibold text-slate-900 mb-2">Terms of Service (Summary)</p>
              <p className="mb-2">By using WeatherProof, you agree to the following:</p>
              <ul className="space-y-1 ml-4">
                <li>• The service provides historical weather data for informational purposes only</li>
                <li>• Reports are not guaranteed to be legally admissible or accepted by third parties</li>
                <li>• You will not misuse the service for fraudulent or unlawful activity</li>
                <li>• Payments for reports are final and non-refundable once generated</li>
                <li>• Service availability is not guaranteed and may change</li>
              </ul>
              <p className="mt-2">WeatherProof reserves the right to update or modify the service at any time.</p>
            </div>
            
            {/* Privacy Policy Summary */}
            <div className="p-4 bg-slate-50 border border-slate-200 text-xs text-slate-600 leading-relaxed">
              <p className="font-semibold text-slate-900 mb-2">Privacy Policy (Summary)</p>
              <p className="mb-2">WeatherProof collects only the information necessary to provide its services:</p>
              <ul className="space-y-1 ml-4">
                <li>• Email address (for account access)</li>
                <li>• Location and date inputs (for weather reports)</li>
                <li>• Payment information is securely handled by third-party providers (e.g., Stripe)</li>
              </ul>
              <p className="mt-2 mb-1">We do not sell or share your personal data.</p>
              <p className="mb-1">Data may be used to:</p>
              <ul className="space-y-1 ml-4 mb-2">
                <li>• Generate reports</li>
                <li>• Improve service performance</li>
                <li>• Provide customer support</li>
              </ul>
              <p className="mb-1">You may request account deletion at any time.</p>
              <p>WeatherProof uses secure connections (HTTPS) to protect your data.</p>
            </div>
            
            {/* Terms Acceptance Checkbox */}
            <div className="flex items-start gap-3">
              <input
                data-testid="terms-checkbox"
                type="checkbox"
                id="acceptTerms"
                checked={acceptedTerms}
                onChange={(e) => setAcceptedTerms(e.target.checked)}
                className="mt-1 w-4 h-4 border-slate-300 focus:ring-2 focus:ring-blue-600"
              />
              <label htmlFor="acceptTerms" className="text-xs text-slate-600 leading-relaxed">
                I agree to the{' '}
                <Link to="/terms" target="_blank" className="text-blue-600 hover:text-blue-700 underline">
                  Terms of Service
                </Link>
                {' '}and{' '}
                <Link to="/privacy" target="_blank" className="text-blue-600 hover:text-blue-700 underline">
                  Privacy Policy
                </Link>
              </label>
            </div>
            
            {/* Data Limitations Disclaimer Checkbox */}
            <div className="flex items-start gap-3">
              <input
                data-testid="disclaimer-checkbox"
                type="checkbox"
                id="acceptDisclaimer"
                checked={acceptedDisclaimer}
                onChange={(e) => setAcceptedDisclaimer(e.target.checked)}
                className="mt-1 w-4 h-4 border-slate-300 focus:ring-2 focus:ring-blue-600"
              />
              <label htmlFor="acceptDisclaimer" className="text-xs text-slate-600 leading-relaxed">
                I understand reports are based on third-party data and may not be accepted by all institutions
              </label>
            </div>
            
            <button
              data-testid="register-submit-btn"
              type="submit"
              disabled={loading || !acceptedTerms || !acceptedDisclaimer}
              className="w-full px-6 py-3 bg-blue-600 text-white hover:bg-blue-700 disabled:bg-slate-300 disabled:cursor-not-allowed transition-colors text-sm font-medium"
            >
              {loading ? 'Creating account...' : 'Create Account'}
            </button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-sm text-slate-600">
              Already have an account?{' '}
              <Link to="/login" className="text-blue-600 hover:text-blue-700 font-medium">
                Log in
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
